import { URL_API_LISTINGS } from "../../constants";
import {
  api,
  CancelToken,
  getApiErrorMessage,
  normalizeListing,
} from "../../helpers";

import dayjs from "dayjs";

const minutesToExpire = 5;

/**
 * Action types
 * @property {string} LISTING_ERROR
 * @property {string} LISTING_LOADING
 * @property {string} LISTING_SUCCESS
 */
export const types = {
  LISTING_ERROR: "LISTING_ERROR",
  LISTING_LOADING: "LISTING_LOADING",
  LISTING_SUCCESS: "LISTING_SUCCESS",
};

/**
 * @namespace Reducers
 */
export const reducers = {
  /**
   * Updates listing error state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.error
   * @memberof Reducers
   */
  listingError: (state = null, action) => {
    switch (action.type) {
      case types.LISTING_ERROR:
        return action.error;
      case types.LISTING_SUCCESS:
        return null;
      default:
        return state;
    }
  },

  /**
   * Updates listing loading state
   * @param {bool} state
   * @param {object} action
   * @param {string} action.type
   * @memberof Reducers
   */
  listingLoading: (state = true, action) => {
    switch (action.type) {
      case types.LISTING_ERROR:
      case types.LISTING_SUCCESS:
        return false;
      case types.LISTING_LOADING:
        return true;
      default:
        return state;
    }
  },

  /**
   * Updates listing state
   * @param {object} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.listings
   * @memberof Reducers
   */
  listings: (state = [], action) => {
    switch (action.type) {
      case types.LISTING_SUCCESS:
        return action.listings;
      default:
        return state;
    }
  },

  /**
   * Updates listings updated state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.updated
   * @memberof Reducers
   */
  listingsUpdated: (state = null, action) => {
    switch (action.type) {
      case types.LISTING_SUCCESS:
        return action.updated;
      default:
        return state;
    }
  },
};

/**
 * @namespace Actions
 */
export const actions = {
  /**
   * Cancel search API request
   * * Updated with a new Axios cancel token after an API request is initiated
   * @memberof Actions
   */
  cancel: () => false,

  /**
   * Clear cached listings
   * @memberof Actions
   */
  clear: () => ({
    type: types.LISTING_SUCCESS,
    listings: [],
    updated: null,
  }),

  /**
   * Fetch listing from API
   * @param {number|string} slug
   * @memberof Actions
   */
  fetch: slug => (dispatch, getState) => {
    const { listingLoading } = getState();

    if (listingLoading) {
      actions.cancel("Canceled");
    }

    dispatch(actions.handleLoading());

    api
      .get(`${URL_API_LISTINGS}/${slug}`, {
        cancelToken: new CancelToken(c => {
          actions.cancel = c;
        }),
      })
      .then(response => {
        if (!!response && !!response.data && !!response.data.data) {
          dispatch(actions.parse(response.data.data));
        } else {
          dispatch(actions.parseError(response));
        }
      })
      .catch(error => {
        dispatch(actions.parseError(error));
      });
  },

  /**
   * Handle error messages
   * @param {string} message
   * @memberof Actions
   */
  handleError: message => ({
    type: types.LISTING_ERROR,
    error: message,
  }),

  /**
   * Handle listings loading events
   * @memberof Actions
   */
  handleLoading: () => ({
    type: types.LISTING_LOADING,
  }),

  /**
   * Handle listing API response
   * @param {array} listing
   * @memberof Actions
   */
  handleSuccess: listings => ({
    type: types.LISTING_SUCCESS,
    listings: listings,
    updated: dayjs().format(),
  }),

  /**
   * Parse listing API response for additional
   * information needed in the UI
   * @param {*} data
   * @memberof Actions
   */
  parse: data => (dispatch, getState) => {
    data = Array.isArray(data) && data.length > 0 ? data[0] : data;
    const newListing = normalizeListing(data) || {};
    newListing.expires = dayjs().add(minutesToExpire, "minutes").format();

    const { listings } = getState();
    let found = false;

    // Add to cache
    let updatedListings = listings.map(listing => {
      if (listing.equipmentId === newListing.equipmentId) {
        found = true;
        return newListing;
      } else {
        return listing;
      }
    });

    if (!found) {
      updatedListings = [...updatedListings, newListing];
    }

    dispatch(actions.handleSuccess(updatedListings));
  },

  /**
   * Parse errors and dispatch error message
   * @param {*} error
   * @memberof Actions
   */
  parseError: error => dispatch => {
    const message = getApiErrorMessage(error);
    dispatch(actions.handleError(message));
  },
};
